import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jsPDF } from 'jspdf';
import Papa from 'papaparse';
import { toast } from 'react-hot-toast';
import type { Proposal } from '../types/proposal';

interface ProposalState {
  proposals: Proposal[];
  activeProposal: string | null;
  loading: boolean;
  error: string | null;
  createProposal: (proposal: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateProposal: (id: string, updates: Partial<Proposal>) => Promise<void>;
  deleteProposal: (id: string) => Promise<void>;
  setActiveProposal: (id: string | null) => void;
  updateSection: (proposalId: string, sectionId: string, content: string) => Promise<void>;
  addTeamMember: (proposalId: string, member: { name: string; role: string; email: string }) => Promise<void>;
  removeTeamMember: (proposalId: string, memberId: string) => Promise<void>;
  exportProposals: (format: 'pdf' | 'csv' | 'excel') => Promise<void>;
}

export const useProposalStore = create<ProposalState>()(
  persist(
    (set, get) => ({
      proposals: [],
      activeProposal: null,
      loading: false,
      error: null,

      createProposal: async (proposalData) => {
        set({ loading: true, error: null });
        try {
          const proposal: Proposal = {
            ...proposalData,
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({
            proposals: [...state.proposals, proposal],
            activeProposal: proposal.id,
          }));
          toast.success('Proposal created successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create proposal';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      updateProposal: async (id, updates) => {
        set({ loading: true, error: null });
        try {
          set((state) => ({
            proposals: state.proposals.map((proposal) =>
              proposal.id === id
                ? {
                    ...proposal,
                    ...updates,
                    updatedAt: new Date().toISOString(),
                  }
                : proposal
            ),
          }));
          toast.success('Proposal updated successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update proposal';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      deleteProposal: async (id) => {
        set({ loading: true, error: null });
        try {
          set((state) => ({
            proposals: state.proposals.filter((proposal) => proposal.id !== id),
            activeProposal: state.activeProposal === id ? null : state.activeProposal,
          }));
          toast.success('Proposal deleted successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete proposal';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      setActiveProposal: (id) => {
        set({ activeProposal: id });
      },

      updateSection: async (proposalId, sectionId, content) => {
        set({ loading: true, error: null });
        try {
          set((state) => ({
            proposals: state.proposals.map((proposal) =>
              proposal.id === proposalId
                ? {
                    ...proposal,
                    sections: proposal.sections.map((section) =>
                      section.id === sectionId
                        ? { ...section, content, status: 'in_progress' }
                        : section
                    ),
                    updatedAt: new Date().toISOString(),
                  }
                : proposal
            ),
          }));
          toast.success('Section updated successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update section';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      addTeamMember: async (proposalId, member) => {
        set({ loading: true, error: null });
        try {
          set((state) => ({
            proposals: state.proposals.map((proposal) =>
              proposal.id === proposalId
                ? {
                    ...proposal,
                    team: [
                      ...proposal.team,
                      { ...member, id: crypto.randomUUID() },
                    ],
                    updatedAt: new Date().toISOString(),
                  }
                : proposal
            ),
          }));
          toast.success('Team member added successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to add team member';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      removeTeamMember: async (proposalId, memberId) => {
        set({ loading: true, error: null });
        try {
          set((state) => ({
            proposals: state.proposals.map((proposal) =>
              proposal.id === proposalId
                ? {
                    ...proposal,
                    team: proposal.team.filter((member) => member.id !== memberId),
                    updatedAt: new Date().toISOString(),
                  }
                : proposal
            ),
          }));
          toast.success('Team member removed successfully');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to remove team member';
          set({ error: message });
          toast.error(message);
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      exportProposals: async (format: 'pdf' | 'csv' | 'excel') => {
        const proposals = get().proposals;
        
        try {
          if (format === 'pdf') {
            const doc = new jsPDF();
            
            // Add title
            doc.setFontSize(16);
            doc.text('Proposals Report', 14, 15);
            doc.setFontSize(10);
            doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 22);

            // Create table data
            const tableData = proposals.map(proposal => [
              proposal.title,
              proposal.status,
              new Date(proposal.deadline).toLocaleDateString(),
              proposal.team.length.toString(),
            ]);

            // Add table
            (doc as any).autoTable({
              startY: 30,
              head: [['Title', 'Status', 'Deadline', 'Team Size']],
              body: tableData,
              theme: 'grid',
              styles: { fontSize: 8, cellPadding: 2 },
              headStyles: { fillColor: [102, 102, 255] },
            });

            // Save PDF
            doc.save(`proposals_${new Date().toISOString().split('T')[0]}.pdf`);
          } else if (format === 'csv') {
            // Export as CSV
            const csvData = Papa.unparse(
              proposals.map(proposal => ({
                Title: proposal.title,
                Status: proposal.status,
                Deadline: new Date(proposal.deadline).toLocaleDateString(),
                'Team Size': proposal.team.length,
                'Created At': new Date(proposal.createdAt).toLocaleDateString(),
                'Updated At': new Date(proposal.updatedAt).toLocaleDateString(),
              }))
            );

            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `proposals_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          } else {
            // Excel export would be implemented here
            toast.error('Excel export coming soon');
          }

          toast.success(`Proposals exported as ${format.toUpperCase()}`);
        } catch (error) {
          console.error('Export failed:', error);
          toast.error('Failed to export proposals');
          throw error;
        }
      },
    }),
    {
      name: 'bidzora-proposals',
      partialize: (state) => ({
        proposals: state.proposals,
      }),
    }
  )
);